/**
 * Torques de aperto — o valor, a sequência de etapas e as imagens.
 *
 * São **6.603 torques com 11.077 etapas**. A média é de 2 etapas por torque e o
 * máximo é 13: um aperto de cabeçote não é um número, é uma sequência (30 N·m,
 * depois 60°, depois mais 60°), e por isso a etapa é uma coleção e não um campo.
 *
 * As imagens vêm de um **banco compartilhado** de 41 figuras — a mesma sequência de
 * cabeçote serve a muitos motores, então ela é cadastrada uma vez e associada. São 395
 * torques com figura associada.
 *
 * **O upload por torque já não mente.** Até 25/09/2026 ele respondia 200, guardava o
 * arquivo em `/dados/conteudo/torques` e não criava linha em `torqueapertoimagem` — a
 * imagem nunca aparecia. Hoje ele cadastra a figura no acervo e associa, na mesma
 * chamada; e o campo do multipart é `File`, que é o que o DTO espera (mandar `arquivo`,
 * como esta tela mandava, devolve 400 antes de o storage ser tocado).
 *
 * **A busca é pelo carro.** Era "Procurar por título…" na faixa do topo, com caixa e
 * acento, e quem procurava "volvo" não achava o torque cuja linha dizia "Volvo Ford 2.0…"
 * — o carro está no motor, não no título. Hoje a API casa cada palavra no título, na peça,
 * no motor e na marca; e o filtro mora no topo da coluna, como em Relés e fusíveis.
 */

import { useEffect, useMemo, useRef, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { obter } from "@/api/cliente"
import {
  type EtapaDoTorque, type FiguraDoAcervo, type ImagemDoTorque, type Torque,
  usarAcervoDeTorques, usarAssociarDoAcervo, usarFilhos, usarGravar, usarIncluir, usarMestre,
  usarPublicacao, usarSubirImagem,
} from "@/api/outrosProjetos"
import { Faixa } from "@/kit/Faixa"
import { BarraDeEstado } from "@/kit/BarraDeEstado"
import { AvisoDeDadoGlobal } from "@/kit/AvisoDeDadoGlobal"
import { ConfirmacaoDigitada } from "@/kit/ConfirmacaoDigitada"
import { MenuDeAcoes } from "@/kit/MenuDeAcoes"
import { SeletorComBusca } from "@/kit/SeletorComBusca"
import { usarOrdemDaLista, usarRecemIncluido } from "@/kit/ordemDaLista"
import { ErroDaApi } from "@/api/erros"
import {
  Aviso, Botao, Campo, ColunaRecolhivel, Escolha, EstadoDePublicacao, FiltroDaLista, ItemDaLista, Janela, Linha,
  Paginacao, Painel, Secao, TabelaSimples, Vazio, anosInvertidos, estiloCampo, mensagem, nomeOu, usarNomes,
  usarTermoAdiado,
} from "./pecas"

const POR_PAGINA = 50

export function Torques() {
  const [busca, setBusca] = useState("")
  const termo = usarTermoAdiado(busca)
  const [ordem, setOrdem] = usarOrdemDaLista("torques")
  const [pagina, setPagina] = useState(1)
  const [aberto, setAberto] = useState<Torque | null>(null)
  const [incluindo, setIncluindo] = useState(false)
  const [recado, setRecado] = useState<string | null>(null)
  const recente = usarRecemIncluido()

  // Busca ou ordem nova começam da primeira página.
  useEffect(() => setPagina(1), [termo, ordem])

  const nomes = usarNomes()
  const lista = usarMestre<Torque>("torques", "api/torques-aperto", {
    busca: termo || undefined,
    ordem: ordem === "alfabetica" ? "alfabetica" : undefined,
    page: pagina, pageSize: POR_PAGINA,
  })

  // A ficha lê o torque pela rota dele, e não a cópia guardada no clique nem a da lista: o
  // torque aberto pode não estar na página ou no filtro de agora (o recém-criado, com a
  // busca em outro carro), e aí o cabeçalho ficava com os dados de antes de gravar. A ficha
  // invalida esta chave ao gravar; excluir não, senão relê o torque que acabou de sumir (404).
  const ficha = useQuery({
    queryKey: ["torque-ficha", aberto?.id] as const,
    enabled: aberto != null,
    queryFn: () => obter<Torque>(`api/torques-aperto/${aberto!.id}`, {}),
  })
  const atual = aberto
    ? ((ficha.data?.id === aberto.id ? ficha.data : undefined)
       ?? lista.itens.find((t) => t.id === aberto.id) ?? aberto)
    : null

  const chaves = [["outros", "torques"]] as const
  const temProxima = lista.itens.length >= POR_PAGINA

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <Faixa titulo="Torques de aperto" detalhe="6.603 no acervo">
        {/* Abrir a inclusão não mexe na seleção: cancelar volta para a tela como estava. */}
        <Botao destaque aoClicar={() => { setRecado(null); setIncluindo(true) }}>
          Incluir torque
        </Botao>
      </Faixa>

      <AvisoDeDadoGlobal oQueMuda="Os torques valem para todo o conteúdo publicado" />

      <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
        {/* A rota devolve lista crua, sem total — o detalhe diz o que a página tem, e não
            finge saber quantos casaram com a busca. */}
        <ColunaRecolhivel chave="torques" titulo="Torques" largura={340}
                          detalhe={lista.isPending ? undefined
                            : `${lista.itens.length}${temProxima ? "+" : ""} ${termo ? "achados" : "nesta página"}`}
                          topo={
                            <FiltroDaLista
                              valor={busca}
                              aoMudar={setBusca}
                              rotulo="Filtrar torques"
                              placeholder="Carro, motor ou peça…"
                              ordem={ordem}
                              aoMudarOrdem={setOrdem}
                            />
                          }>
          {lista.isPending ? (
            <p style={{ fontSize: 12.5, color: "var(--txt2)" }}>Carregando…</p>
          ) : lista.error ? (
            <p style={{ fontSize: 12.5, color: "var(--erro)" }}>
              {lista.error instanceof ErroDaApi ? lista.error.message : "Não foi possível ler."}
            </p>
          ) : lista.itens.length === 0 ? (
            <p style={{ fontSize: 12.5, color: "var(--txt3)" }}>
              {termo ? "Nenhum torque com esse texto no título, na peça, no motor ou na marca." : "Nenhum torque."}
            </p>
          ) : (
            <>
              {lista.itens.map((t) => (
                <ItemDaLista
                  key={t.id}
                  ativo={atual?.id === t.id}
                  marcado={t.publicado}
                  recente={recente.tem(t.id)}
                  aoClicar={() => setAberto(t)}
                  titulo={t.titulo || t.texto || `#${t.id}`}
                  detalhe={`${nomeOu(nomes.motor, t.motorId, "motor")} · ${faixaDeAnos(t)}`}
                />
              ))}
              <Paginacao pagina={pagina} aoMudar={setPagina} temProxima={temProxima} />
            </>
          )}
        </ColunaRecolhivel>

        {atual ? (
          <FichaDoTorque
            key={atual.id}
            torque={atual}
            nomes={nomes}
            chaves={chaves}
            aoAvisar={setRecado}
            aoFechar={() => setAberto(null)}
          />
        ) : (
          <Vazio>
            Escolha um torque à esquerda. São 6.603 no total — procure pelo carro, pelo motor ou
            pela peça: "volvo xc60", "fire cabeçote".
          </Vazio>
        )}
      </div>

      {incluindo && (
        <InclusaoDoTorque
          nomes={nomes}
          chaves={chaves}
          aoAvisar={setRecado}
          aoFechar={() => setIncluindo(false)}
          aoCriar={(t) => { setIncluindo(false); setAberto(t); recente.marcar(t.id) }}
        />
      )}

      <BarraDeEstado direita="6.603 torques no acervo, 11.077 etapas">
        {recado ?? ""}
      </BarraDeEstado>
    </div>
  )
}

/** "2012–2016", "2012" quando é um ano só, "?" no lado que falta. */
function faixaDeAnos(t: { anoInicial: number; anoFinal: number }): string {
  if (!t.anoInicial && !t.anoFinal) return "sem ano"
  if (t.anoInicial === t.anoFinal) return String(t.anoInicial)
  return `${t.anoInicial || "?"}–${t.anoFinal || "?"}`
}

/**
 * Incluir torque.
 *
 * **Três campos, e não os nove da ficha.** Motor e sistema são obrigatórios na API
 * (`MotorId é obrigatório e deve ser maior que 0`, medido em 25/09/2026 contra o
 * PostgreSQL); título também. O resto — texto, restrição, faixa de ano, parafusos —
 * fica para a ficha, que abre em seguida com o registro já criado. Dois formulários
 * com os mesmos nove campos é o caminho para um deles envelhecer sozinho.
 *
 * Nasce **fora do ar** de propósito. Publicar é botão próprio na ficha, com nome que
 * diz o que vai acontecer; interruptor no meio de um formulário de cadastro é
 * exatamente o que se esquece de marcar — só que aqui o esquecimento é ao contrário,
 * e publicaria conteúdo pela metade para quem paga.
 *
 * Abre numa `Janela` por cima, como a inclusão de Relés e fusíveis: o torque que estava
 * aberto continua lá embaixo, e cancelar é só fechar.
 */
function InclusaoDoTorque({ nomes, chaves, aoAvisar, aoFechar, aoCriar }: {
  nomes: ReturnType<typeof usarNomes>
  chaves: readonly (readonly unknown[])[]
  aoAvisar: (m: string | null) => void
  aoFechar: () => void
  aoCriar: (t: Torque) => void
}) {
  const incluir = usarIncluir<Torque>(chaves.map((c) => [...c]))
  const [forma, setForma] = useState({ titulo: "", motorId: 0, sistemaId: 0 })

  const falta = !forma.titulo.trim() || forma.motorId <= 0 || forma.sistemaId <= 0

  return (
    <Janela titulo="Incluir torque" aoFechar={aoFechar} largura={620}>
      <Secao titulo="O mínimo para o torque existir">
        <Campo rotulo="Título" ajuda="Costuma trazer o veículo e a peça — “XC60 2.0 — Cabeçote do motor”.">
          <input
            autoFocus
            value={forma.titulo}
            maxLength={150}
            onChange={(e) => setForma({ ...forma, titulo: e.target.value })}
            style={estiloCampo}
          />
        </Campo>
        <Linha>
          <Escolha
            rotulo="Motor"
            mapa={nomes.motor}
            valor={forma.motorId}
            aoMudar={(v) => setForma({ ...forma, motorId: v })}
            prefixo="motor"
          />
          <Escolha
            rotulo="Sistema"
            mapa={nomes.sistema}
            valor={forma.sistemaId}
            aoMudar={(v) => setForma({ ...forma, sistemaId: v })}
            prefixo="sistema"
          />
        </Linha>

        <p style={{ margin: "2px 0 10px", fontSize: 11, color: "var(--txt3)",
                    lineHeight: 1.5, maxWidth: "70ch" }}>
          O torque nasce <b>fora do ar</b>: ninguém que paga vê até você publicar. Depois de
          criar, a ficha abre para as etapas do aperto e as imagens — um torque sem etapa não
          diz nada.
        </p>

        <div style={{ display: "flex", gap: 6 }}>
          <Botao
            destaque
            ocupado={incluir.isPending}
            desligado={falta}
            aoClicar={() => {
              aoAvisar(null)
              incluir.mutate({
                rota: "api/torques-aperto",
                dados: {
                  titulo: forma.titulo.trim(), motorId: forma.motorId, sistemaId: forma.sistemaId,
                  texto: "", restricao: "", anoInicial: 0, anoFinal: 0, quantidadeParafusos: 0,
                },
              }, {
                onSuccess: (t) => {
                  aoAvisar(`Torque ${t.id} criado, fora do ar. Acrescente as etapas.`)
                  aoCriar(t)
                },
                onError: (e) => aoAvisar(mensagem(e)),
              })
            }}
          >
            {incluir.isPending ? "Criando…" : "Criar o torque"}
          </Botao>
          <Botao aoClicar={aoFechar}>Cancelar</Botao>
        </div>
      </Secao>
    </Janela>
  )
}

/**
 * Associar ao torque uma figura que já existe no acervo.
 *
 * É a operação que faltava: as 41 figuras estavam lá e nenhuma tela sabia usá-las, então
 * o jeito de pôr desenho num torque era subir cópia — e o upload, além de duplicar, não
 * liga nada.
 *
 * A ordem tem padrão: a próxima livre. Quem associa quer a figura na sequência, não
 * quer escolher um número.
 */
function AssociarDoAcervo({ torque, figuras, carregando, invalidar, aoAvisar, proximaOrdem }: {
  torque: Torque
  figuras: FiguraDoAcervo[]
  carregando: boolean
  invalidar: readonly unknown[][]
  aoAvisar: (m: string | null) => void
  proximaOrdem: number
}) {
  const associar = usarAssociarDoAcervo(invalidar)
  const [escolhida, setEscolhida] = useState(0)

  const opcoes = useMemo(
    () => figuras.map((f) => ({ id: f.id, rotulo: nomeDaFigura(f) })),
    [figuras],
  )

  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 6, margin: "0 0 10px" }}>
      <div style={{ flex: "0 1 360px", minWidth: 0 }}>
        <Campo rotulo={`Figura do acervo (${figuras.length})`}>
          <SeletorComBusca
            itens={opcoes}
            valor={escolhida}
            aoMudar={setEscolhida}
            carregando={carregando}
            rotuloAcessivel="Figura do acervo"
            placeholder="Digite para procurar uma figura…"
          />
        </Campo>
      </div>
      <div style={{ marginBottom: 9 }}>
        <Botao
          ocupado={associar.isPending}
          desligado={escolhida <= 0}
          aoClicar={() => {
            aoAvisar(null)
            associar.mutate({ torqueId: torque.id, figuraId: escolhida, ordem: proximaOrdem }, {
              onSuccess: () => { aoAvisar("Figura associada ao torque."); setEscolhida(0) },
              onError: (e) => aoAvisar(mensagem(e)),
            })
          }}
        >
          {associar.isPending ? "Associando…" : "Associar"}
        </Botao>
      </div>
    </div>
  )
}

function nomeDaFigura(f: FiguraDoAcervo): string {
  return f.titulo?.trim() || f.imagem || `figura ${f.id}`
}

/** O que a ficha edita e o PUT leva. */
const CAMPOS_DO_TORQUE = ["titulo", "texto", "restricao", "motorId", "sistemaId", "anoInicial", "anoFinal",
                          "quantidadeParafusos"] as const

/** Uma etapa como o formulário a edita — a mesma forma para incluir e para alterar. */
type FormaDaEtapa = { etapa: number; texto: string; restricao: string; grandezaId: number }

/**
 * A unidade da etapa — `grandeza_id` no banco, obrigatória na API (`GrandezaId > 0`).
 *
 * **A etapa é valor e unidade, não texto livre.** No acervo, `texto` guarda só o número
 * ("28", "90") e a unidade vem daqui: Nm em 8.171 etapas, graus em 2.750 (medido em
 * 26/10/2026). A tela mandava `grandezaId: 0` e um texto como "30 N·m, depois 60°", e a API
 * recusava com 400 — incluir etapa por esta tela nunca tinha funcionado. São as unidades
 * de torque do cadastro `grandeza`; a API não tem rota que o liste.
 */
const UNIDADES: { id: number; rotulo: string; sufixo: string }[] = [
  { id: 79, rotulo: "Nm (newton metro)", sufixo: " Nm" },
  { id: 87, rotulo: "° (graus)", sufixo: "°" },
  { id: 81, rotulo: "kgf·m", sufixo: " kgf·m" },
  { id: 80, rotulo: "lbf·ft", sufixo: " lbf·ft" },
]
const NM = 79

/** "28 Nm", "90°". Etapa antiga sem unidade (há 140) mostra só o texto. */
function valorDaEtapa(e: { texto: string; grandezaId: number }): string {
  const u = UNIDADES.find((x) => x.id === e.grandezaId)
  return u ? `${e.texto}${u.sufixo}` : e.texto
}

const ETAPA_VAZIA: FormaDaEtapa = { etapa: 0, texto: "", restricao: "", grandezaId: NM }

/**
 * A ficha do torque.
 *
 * Organizada pelo que se faz nela: o cabeçalho diz **qual torque é** (motor, sistema,
 * anos) sem precisar ler os campos; o que apaga fica num menu e pede o título digitado
 * (era um "Excluir" ao lado do "Gravar", um clique só); o "Gravar" só acende quando há o
 * que gravar; e etapas e imagens se corrigem no lugar, em vez de apagar e incluir de novo.
 */
function FichaDoTorque({ torque, nomes, chaves, aoAvisar, aoFechar }: {
  torque: Torque
  nomes: ReturnType<typeof usarNomes>
  chaves: readonly (readonly unknown[])[]
  aoAvisar: (m: string | null) => void
  aoFechar: () => void
}) {
  const base = `api/torques-aperto/${torque.id}`
  const etapas = usarFilhos<EtapaDoTorque>(["torque-etapas", torque.id], `${base}/etapas`)
  const imagens = usarFilhos<ImagemDoTorque>(["torque-imagens", torque.id], `${base}/imagens`)
  const acervo = usarAcervoDeTorques()

  const invalidar = [...chaves.map((c) => [...c]), ["torque-ficha", torque.id],
                     ["outros-filhos", "torque-etapas", torque.id], ["outros-filhos", "torque-imagens", torque.id]]
  const publicacao = usarPublicacao(invalidar)
  const { alterar, criar, excluir } = usarGravar(invalidar)
  // Excluir o torque renova só a lista: o resto é dele, e ele não existe mais.
  const { excluir: excluirTorque } = usarGravar(chaves.map((c) => [...c]))
  const subir = usarSubirImagem(invalidar)

  // O formulário só recomeça quando o que está GRAVADO muda (gravou, ou outro torque), e não
  // a cada objeto novo: a releitura do torque pela rota dele chega depois de a ficha abrir,
  // e recomeçar por referência apagava o que a pessoa já tinha digitado.
  const [forma, setForma] = useState({ ...torque })
  const gravado = JSON.stringify(CAMPOS_DO_TORQUE.map((k) => torque[k] ?? ""))
  useEffect(() => setForma({ ...torque }), [torque.id, gravado])

  const mudou = CAMPOS_DO_TORQUE.some((k) => (forma[k] ?? "") !== (torque[k] ?? ""))
  const invertidos = anosInvertidos(forma)

  const [excluindo, setExcluindo] = useState(false)
  const nomeDoTorque = torque.titulo?.trim() || `Torque ${torque.id}`

  const gravar = () => {
    if (!mudou || invertidos || alterar.isPending) return
    aoAvisar(null)
    // Só os campos do PUT: a rota do torque devolve também etapas e imagens, e texto e
    // restrição nulos (a entidade crua), que o DTO não aceita.
    const { titulo, texto, restricao, motorId, sistemaId, anoInicial, anoFinal, quantidadeParafusos } = forma
    const dados = {
      titulo: titulo ?? "", texto: texto ?? "", restricao: restricao ?? "",
      motorId, sistemaId, anoInicial, anoFinal, quantidadeParafusos,
    }
    alterar.mutate({ rota: base, dados }, {
      onSuccess: () => aoAvisar("Torque gravado."),
      onError: (e) => aoAvisar(mensagem(e)),
    })
  }

  // ---- etapas
  const ordenadas = useMemo(
    () => [...(etapas.data ?? [])].sort((a, b) => (a.etapa - b.etapa) || (a.id - b.id)),
    [etapas.data],
  )
  const proximaEtapa = ordenadas.reduce((m, e) => Math.max(m, e.etapa || 0), 0) + 1
  const [novaEtapa, setNovaEtapa] = useState<FormaDaEtapa>(ETAPA_VAZIA)
  const textoDaNova = useRef<HTMLInputElement>(null)
  const [etapaAberta, setEtapaAberta] = useState<EtapaDoTorque | null>(null)
  const [removendoEtapa, setRemovendoEtapa] = useState<EtapaDoTorque | null>(null)
  const recente = usarRecemIncluido()

  const incluirEtapa = () => {
    if (!novaEtapa.texto.trim() || novaEtapa.grandezaId <= 0 || criar.isPending) return
    aoAvisar(null)
    // Sem número, vai para o fim da sequência: é o que quem inclui uma a uma quer.
    const dados = { ...novaEtapa, etapa: novaEtapa.etapa || proximaEtapa }
    criar.mutate({ rota: `${base}/etapas`, dados }, {
      onSuccess: (criada) => {
        // A unidade fica: quem inclui 20 Nm, 30 Nm em sequência não quer escolher de novo.
        setNovaEtapa({ ...ETAPA_VAZIA, grandezaId: novaEtapa.grandezaId })
        aoAvisar(`Etapa ${dados.etapa} incluída.`)
        recente.marcar((criada as { etapaId?: number } | undefined)?.etapaId)
        textoDaNova.current?.focus()
      },
      onError: (e) => aoAvisar(mensagem(e)),
    })
  }

  const gravarEtapa = () => {
    if (!etapaAberta || !etapaAberta.texto.trim() || etapaAberta.grandezaId <= 0 || alterar.isPending) return
    aoAvisar(null)
    const { etapa, texto, restricao, grandezaId } = etapaAberta
    alterar.mutate({ rota: `${base}/etapas/${etapaAberta.id}`, dados: { etapa, texto, restricao, grandezaId } }, {
      onSuccess: () => { aoAvisar(`Etapa ${etapa} gravada.`); setEtapaAberta(null) },
      onError: (e) => aoAvisar(mensagem(e)),
    })
  }

  // ---- imagens
  const figuraPorId = useMemo(() => new Map((acervo.data ?? []).map((f) => [f.id, f])), [acervo.data])
  const [desassociando, setDesassociando] = useState<ImagemDoTorque | null>(null)
  const imagensNaTela = useMemo(
    () => [...(imagens.data ?? [])]
      .sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0))
      .map((i) => {
        const fig = i.bancoTorqueImgId ? figuraPorId.get(i.bancoTorqueImgId) : undefined
        return { ...i, figura: fig ? nomeDaFigura(fig) : "", arquivo: i.imagem ?? i.arquivo ?? fig?.imagem ?? "" }
      }),
    [imagens.data, figuraPorId],
  )

  return (
    <Painel>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 12 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
            <b style={{ fontSize: 13.5 }}>{nomeDoTorque}</b>
            <span className="mono" style={{ fontSize: 11, color: "var(--txt3)" }}>{torque.id}</span>
          </div>
          <div style={{ marginTop: 3, fontSize: 11, color: "var(--txt2)" }}>
            {nomeOu(nomes.motor, torque.motorId, "motor")} · {nomeOu(nomes.sistema, torque.sistemaId, "sistema")}
            {" · "}{faixaDeAnos(torque)}
            {torque.quantidadeParafusos ? ` · ${torque.quantidadeParafusos} parafusos` : ""}
          </div>
        </div>
        <MenuDeAcoes
          rotulo="Ações do torque"
          acoes={[
            {
              rotulo: "Excluir torque",
              dica: "Apaga o torque com as etapas",
              perigo: true,
              aoEscolher: () => { aoAvisar(null); setExcluindo(true) },
            },
          ]}
        />
        <button onClick={aoFechar} aria-label="Fechar" style={{
          border: 0, background: "transparent", cursor: "pointer",
          color: "var(--txt3)", fontSize: 16, lineHeight: 1, paddingTop: 4,
        }}>×</button>
      </div>

      {excluindo && (
        <div style={{ marginBottom: 12 }}>
          <ConfirmacaoDigitada
            alvo={nomeDoTorque}
            oQueAcontece={
              <>
                Exclui o torque <b>{nomeDoTorque}</b>
                {ordenadas.length > 0 && <>, com as suas <b>{ordenadas.length} etapa{ordenadas.length === 1 ? "" : "s"}</b></>}.
                As figuras do acervo continuam lá. <b>Não há como desfazer pela tela.</b>
              </>
            }
            rotuloDoBotao="Excluir o torque"
            ocupado={excluirTorque.isPending}
            aoCancelar={() => setExcluindo(false)}
            aoConfirmar={() => {
              aoAvisar(null)
              excluirTorque.mutate(base, {
                onSuccess: () => { aoAvisar(`Torque ${torque.id} excluído.`); aoFechar() },
                onError: (e) => { aoAvisar(mensagem(e)); setExcluindo(false) },
              })
            }}
          />
        </div>
      )}

      <EstadoDePublicacao
        publicado={torque.publicado}
        oQue="o torque"
        ocupado={publicacao.isPending}
        aoTrocar={(publicar) => {
          aoAvisar(null)
          publicacao.mutate({ base, publicar }, {
            onSuccess: () => aoAvisar(publicar ? "Torque no ar." : "Torque fora do ar."),
            onError: (e) => aoAvisar(mensagem(e)),
          })
        }}
      />

      <Secao titulo="O torque">
        <form
          onSubmit={(e) => { e.preventDefault(); gravar() }}
          onKeyDown={(e) => { if (e.key === "Escape" && mudou) setForma({ ...torque }) }}
        >
          <Campo rotulo="Título" ajuda="O que aparece na lista: o veículo e a peça.">
            <input value={forma.titulo ?? ""} maxLength={150} style={estiloCampo}
                   onChange={(e) => setForma({ ...forma, titulo: e.target.value })} />
          </Campo>
          <Linha>
            <Campo rotulo="Peça">
              <input value={forma.texto ?? ""} style={estiloCampo}
                     onChange={(e) => setForma({ ...forma, texto: e.target.value })} />
            </Campo>
            <Campo rotulo="Restrição" ajuda="Quando o torque só vale em parte dos casos.">
              <input value={forma.restricao ?? ""} style={estiloCampo}
                     onChange={(e) => setForma({ ...forma, restricao: e.target.value })} />
            </Campo>
          </Linha>
          <Linha>
            <Escolha rotulo="Motor" mapa={nomes.motor} prefixo="motor"
                     valor={forma.motorId} aoMudar={(v) => setForma({ ...forma, motorId: v })} />
            <Escolha rotulo="Sistema" mapa={nomes.sistema} prefixo="sistema"
                     valor={forma.sistemaId} aoMudar={(v) => setForma({ ...forma, sistemaId: v })} />
          </Linha>
          <div style={{ display: "flex", gap: 9, flexWrap: "wrap", maxWidth: 460 }}>
            <Campo rotulo="Ano inicial">
              <input type="number" value={forma.anoInicial || ""} style={estiloCampo}
                     onChange={(e) => setForma({ ...forma, anoInicial: Number(e.target.value) })} />
            </Campo>
            <Campo rotulo="Ano final">
              <input type="number" value={forma.anoFinal || ""} style={estiloCampo}
                     onChange={(e) => setForma({ ...forma, anoFinal: Number(e.target.value) })} />
            </Campo>
            <Campo rotulo="Parafusos" ajuda="Quantidade na peça.">
              <input type="number" value={forma.quantidadeParafusos || ""} style={estiloCampo}
                     onChange={(e) => setForma({ ...forma, quantidadeParafusos: Number(e.target.value) })} />
            </Campo>
          </div>

          {invertidos && <Aviso>O ano inicial é depois do ano final — a API recusaria.</Aviso>}

          <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 4 }}>
            <Botao destaque envia ocupado={alterar.isPending} desligado={!mudou || invertidos} aoClicar={() => {}}>
              {alterar.isPending ? "Gravando…" : "Gravar"}
            </Botao>
            {mudou && (
              <>
                <Botao aoClicar={() => setForma({ ...torque })}>Descartar</Botao>
                <span style={{ fontSize: 11, color: "var(--txt3)" }}>Alterações não gravadas.</span>
              </>
            )}
          </div>
        </form>
      </Secao>

      <Secao titulo="Etapas do aperto" contagem={`${ordenadas.length}`}>
        <p style={{ margin: "-2px 0 7px", fontSize: 10.5, color: "var(--txt3)", lineHeight: 1.5, maxWidth: "76ch" }}>
          A ordem é o aperto: cada etapa é um passo da sequência, com valor e unidade (20 Nm,
          depois 90°). Clique numa etapa para corrigir.
        </p>

        <TabelaSimples
          itens={ordenadas}
          vazio="Sem etapas. Um torque de valor único costuma ter uma só."
          recente={recente.tem}
          selecionado={etapaAberta?.id ?? null}
          aoAbrir={(e) => { aoAvisar(null); setRemovendoEtapa(null); setEtapaAberta(e) }}
          abaixoDe={(e) => removendoEtapa?.id === e.id && (
            <ConfirmarNaLinha
              aoConfirmar={() => {
                aoAvisar(null)
                excluir.mutate(`${base}/etapas/${e.id}`, {
                  onSuccess: () => {
                    aoAvisar(`Etapa ${e.etapa} removida.`)
                    setRemovendoEtapa(null)
                    setEtapaAberta((a) => (a?.id === e.id ? null : a))
                  },
                  onError: (x) => { aoAvisar(mensagem(x)); setRemovendoEtapa(null) },
                })
              }}
              aoCancelar={() => setRemovendoEtapa(null)}
              ocupado={excluir.isPending}
              rotulo="Remover a etapa"
            >
              Remover a etapa <b>{e.etapa}</b> ({valorDaEtapa(e)})?
            </ConfirmarNaLinha>
          )}
          colunas={[
            { chave: "etapa", rotulo: "Etapa", numero: true, largura: 70 },
            { chave: "texto", rotulo: "Valor", render: (e) => valorDaEtapa(e) },
            { chave: "restricao", rotulo: "Restrição", largura: 190 },
            {
              chave: "id", rotulo: "", largura: 40,
              render: (e) => (
                <BotaoX
                  rotulo={`Remover a etapa ${e.etapa}`}
                  aoClicar={() => { aoAvisar(null); setRemovendoEtapa(e) }}
                />
              ),
            },
          ]}
        />

        {etapaAberta ? (
          <div style={{ marginTop: 10 }}>
            <Secao titulo={`Alterar etapa ${etapaAberta.etapa}`}>
              <form onSubmit={(e) => { e.preventDefault(); gravarEtapa() }}
                    onKeyDown={(e) => { if (e.key === "Escape") setEtapaAberta(null) }}>
                <CamposDaEtapa valor={etapaAberta} aoMudar={(p) => setEtapaAberta({ ...etapaAberta, ...p })} />
                <div style={{ display: "flex", gap: 6 }}>
                  <Botao destaque envia ocupado={alterar.isPending} desligado={!etapaAberta.texto.trim() || etapaAberta.grandezaId <= 0} aoClicar={() => {}}>
                    {alterar.isPending ? "Gravando…" : "Gravar etapa"}
                  </Botao>
                  <Botao aoClicar={() => setEtapaAberta(null)}>Cancelar</Botao>
                </div>
              </form>
            </Secao>
          </div>
        ) : (
          <form style={{ marginTop: 10 }} onSubmit={(e) => { e.preventDefault(); incluirEtapa() }}>
            <CamposDaEtapa valor={novaEtapa} aoMudar={(p) => setNovaEtapa({ ...novaEtapa, ...p })}
                           proxima={proximaEtapa} refTexto={textoDaNova} />
            <Botao envia desligado={!novaEtapa.texto.trim() || novaEtapa.grandezaId <= 0} ocupado={criar.isPending} aoClicar={() => {}}>
              Incluir etapa
            </Botao>
          </form>
        )}
      </Secao>

      <Secao titulo="Imagens" contagem={`${imagensNaTela.length}`}>
        <p style={{ margin: "-2px 0 7px", fontSize: 10.5, color: "var(--txt3)", lineHeight: 1.5, maxWidth: "76ch" }}>
          O caminho normal é <b>associar do acervo</b>: são 41 figuras servindo 395 torques,
          porque a mesma sequência de cabeçote vale para muitos motores.
        </p>

        {acervo.error ? (
          <p style={{ margin: "0 0 8px", fontSize: 11.5, color: "var(--erro)" }}>
            {acervo.error instanceof ErroDaApi ? acervo.error.message : "Não foi possível ler o acervo de figuras."}
          </p>
        ) : (
          <AssociarDoAcervo
            torque={torque}
            figuras={acervo.data ?? []}
            carregando={acervo.isPending}
            invalidar={invalidar}
            aoAvisar={aoAvisar}
            proximaOrdem={imagensNaTela.reduce((m, i) => Math.max(m, i.ordem ?? 0), 0) + 1}
          />
        )}

        <TabelaSimples
          itens={imagensNaTela}
          vazio="Sem imagem associada."
          abaixoDe={(i) => desassociando?.id === i.id && (
            <ConfirmarNaLinha
              aoConfirmar={() => {
                aoAvisar(null)
                excluir.mutate(`${base}/imagens/${i.id}`, {
                  onSuccess: () => { aoAvisar("Figura desassociada. Ela continua no acervo."); setDesassociando(null) },
                  onError: (x) => { aoAvisar(mensagem(x)); setDesassociando(null) },
                })
              }}
              aoCancelar={() => setDesassociando(null)}
              ocupado={excluir.isPending}
              rotulo="Desassociar"
            >
              Tirar <b>{i.figura || i.arquivo || `a imagem ${i.id}`}</b> deste torque? A figura continua
              no acervo para os outros torques.
            </ConfirmarNaLinha>
          )}
          colunas={[
            { chave: "ordem", rotulo: "Ordem", numero: true, largura: 70 },
            { chave: "figura", rotulo: "Figura do acervo" },
            { chave: "arquivo", rotulo: "Arquivo", largura: 220 },
            {
              chave: "id", rotulo: "", largura: 40,
              render: (i) => (
                <BotaoX rotulo={`Desassociar ${i.figura || i.arquivo || i.id}`}
                        aoClicar={() => { aoAvisar(null); setDesassociando(i) }} />
              ),
            },
          ]}
        />

        <label style={{
          display: "inline-flex", alignItems: "center", gap: 7, marginTop: 8,
          fontSize: 11.5, cursor: "pointer", color: "var(--txt2)",
        }}>
          Ou envie uma figura nova (entra no acervo e já fica associada):
          <input
            type="file"
            accept="image/*"
            style={{ fontSize: 11 }}
            onChange={(e) => {
              const arquivo = e.target.files?.[0]
              if (!arquivo) return
              aoAvisar(null)
              subir.mutate({ rota: `${base}/imagem`, arquivo, campo: "File" }, {
                onSuccess: () => aoAvisar(
                  `${arquivo.name} enviada, cadastrada no acervo e associada a este torque.`),
                onError: (x) => aoAvisar(mensagem(x)),
              })
              e.target.value = ""
            }}
          />
          {subir.isPending && <span style={{ color: "var(--txt2)" }}>enviando…</span>}
        </label>
      </Secao>
    </Painel>
  )
}

/** Os campos de uma etapa — os mesmos para incluir e para alterar. */
function CamposDaEtapa({ valor, aoMudar, proxima, refTexto }: {
  valor: FormaDaEtapa
  aoMudar: (p: Partial<FormaDaEtapa>) => void
  /** Na inclusão: o número que a etapa leva se ficar em branco. */
  proxima?: number
  refTexto?: React.Ref<HTMLInputElement>
}) {
  return (
    <Linha>
      <div style={{ flex: "0 0 110px" }}>
        <Campo rotulo="Nº da etapa">
          <input type="number" value={valor.etapa || ""} style={estiloCampo}
                 placeholder={proxima ? String(proxima) : undefined}
                 onChange={(e) => aoMudar({ etapa: Number(e.target.value) })} />
        </Campo>
      </div>
      <div style={{ flex: "1 1 140px" }}>
        <Campo rotulo="Valor">
          <input ref={refTexto} value={valor.texto} style={estiloCampo}
                 placeholder={valor.grandezaId === 87 ? "90" : "30"}
                 onChange={(e) => aoMudar({ texto: e.target.value })} />
        </Campo>
      </div>
      <div style={{ flex: "1 1 170px" }}>
        <Campo rotulo="Unidade">
          <select value={valor.grandezaId} style={estiloCampo}
                  onChange={(e) => aoMudar({ grandezaId: Number(e.target.value) })}>
            {/* Etapa antiga gravada sem unidade: tem de escolher para gravar. */}
            {!UNIDADES.some((u) => u.id === valor.grandezaId) && (
              <option value={valor.grandezaId}>
                {valor.grandezaId > 0 ? `grandeza ${valor.grandezaId}` : "Escolha…"}
              </option>
            )}
            {UNIDADES.map((u) => <option key={u.id} value={u.id}>{u.rotulo}</option>)}
          </select>
        </Campo>
      </div>
      <div style={{ flex: "1 1 180px" }}>
        <Campo rotulo="Restrição">
          <input value={valor.restricao ?? ""} style={estiloCampo}
                 onChange={(e) => aoMudar({ restricao: e.target.value })} />
        </Campo>
      </div>
    </Linha>
  )
}

/** O × de uma linha da tabela. Não abre a linha junto: a linha inteira abre a edição. */
function BotaoX({ rotulo, aoClicar }: { rotulo: string; aoClicar: () => void }) {
  return (
    <button
      type="button"
      aria-label={rotulo}
      title={rotulo}
      onClick={(e) => { e.stopPropagation(); aoClicar() }}
      style={{
        border: 0, background: "transparent", cursor: "pointer",
        color: "var(--txt3)", fontSize: 13, lineHeight: 1,
      }}
    >
      ×
    </button>
  )
}

/** A pergunta "tem certeza?" colada embaixo da linha, como em Relés e fusíveis. */
function ConfirmarNaLinha({ children, rotulo, ocupado, aoConfirmar, aoCancelar }: {
  children: React.ReactNode
  rotulo: string
  ocupado: boolean
  aoConfirmar: () => void
  aoCancelar: () => void
}) {
  return (
    <div
      role="alert"
      onKeyDown={(e) => { if (e.key === "Escape") aoCancelar() }}
      style={{
        display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8,
        padding: "7px 10px", borderRadius: 6, border: "1px solid var(--erro)",
        background: "color-mix(in srgb, var(--erro) 5%, transparent)", fontSize: 11.5,
        whiteSpace: "normal",
      }}
    >
      <span style={{ flex: 1, minWidth: 220, lineHeight: 1.5 }}>{children}</span>
      <Botao ocupado={ocupado} aoClicar={aoConfirmar}>{rotulo}</Botao>
      <Botao aoClicar={aoCancelar}>Não</Botao>
    </div>
  )
}
